import { TestId } from '@modules/utilities/constants/testConstants';
import { Button, Link } from '@rbx/ui';
import { EStudioTaskType, StudioDialogParams } from '@rbx/studio';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@rbx/intl';
import IncompatibleStudioPlatformDialog from '@modules/useStudio/components/IncompatibleStudioPlatformDialog';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import UseStudioCTA, { UseStudioVariant } from './UseStudioCTA';
import useOpenStudio from '../hooks/useOpenStudio';

export interface UseStudioButtonProps {
  variant?: UseStudioVariant;
  assetId?: number | string;
  placeId?: string;
  universeId?: string;
  // - when true, for users on an incompatible platform (e.g. mobile), instead of
  //   a button, a message will be shown that the platform is not supported.
  // - when false, nothing will be shown for users on an incompatible platform.
  buttonProps?: React.ComponentProps<typeof Button>;
  buttonTextTranslationKey?: string; // default: 'Action.DownloadStudio'
  // define custom button text in translations namespace. Gets overriden by children.
  buttonStyle?: Record<string, string | number>;
  iconStyle?: Record<string, string | number>;
  children?: React.ReactNode; // button contents. Overrides buttonTextTranslationKey
}

export default function UseStudioButton({
  variant = 'iconButton',
  assetId,
  placeId,
  universeId,
  // TODO (CRK-3951, @ahua): remove once TranslKey component is available
  buttonTextTranslationKey = 'Action.DownloadStudio',
  children,
  buttonProps,
  buttonStyle,
  iconStyle,
  ...otherCTAProps
}: UseStudioButtonProps &
  (React.ComponentProps<typeof Button> | React.ComponentProps<typeof Link>)) {
  const [openIncompatibleDialog, setOpenIncompatibleDialog] = useState(false);
  const { translate } = useTranslation();
  const [hasStudioDialog, setHasStudioDialog] = useState<boolean>(false);
  const { isCompatible, open, dialog: studioDialog } = useOpenStudio();

  const onClick = useCallback(() => {
    unifiedLoggerClient.logClickEvent({
      eventName: DocsSiteEventType.OpenStudioButtonClick,
      parameters: {
        assetId: assetId?.toString() ?? '',
        placeId: placeId?.toString() ?? '',
        universeId: universeId?.toString() ?? '',
      },
    });
    if (!isCompatible) {
      setOpenIncompatibleDialog(true);
      return;
    }
    let params: StudioDialogParams = {
      task: EStudioTaskType.Default,
    };
    if (assetId) {
      params = {
        task: EStudioTaskType.ViewAsset,
        assetId: String(assetId),
      };
    }
    if (placeId && universeId) {
      params = {
        task: EStudioTaskType.EditPlace,
        placeId,
        universeId,
      };
    }
    open(params);
  }, [isCompatible, assetId, placeId, universeId, open]);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.document.querySelectorAll(`[data-testid="${TestId.StudioDownloadDialog}"]`).length > 0
    ) {
      setHasStudioDialog(true);
    }
  }, []);
  return (
    <React.Fragment>
      <UseStudioCTA
        variant={variant}
        buttonProps={buttonProps}
        text={buttonTextTranslationKey && translate(buttonTextTranslationKey)}
        onClick={onClick}
        buttonStyle={buttonStyle}
        iconStyle={iconStyle}
        {...otherCTAProps}>
        {children}
      </UseStudioCTA>
      {isCompatible && !hasStudioDialog && studioDialog}
      {!isCompatible && (
        <IncompatibleStudioPlatformDialog
          open={openIncompatibleDialog}
          setOpen={setOpenIncompatibleDialog}
        />
      )}
    </React.Fragment>
  );
}
